import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ClipboardList,
  Cog,
  Cpu,
  Droplet,
  FileText,
  Gauge,
  Globe,
  MapPin,
  Plug,
  Radio,
  Server,
  ShieldCheck,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";

const BANNER_URL =
  "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/assets/rick-field-banner.webp";

const SKY_DARK = "#3B82C8";
const BG = "#F2F7FD";
const BG2 = "#E5EEF9";
const LINE = "#D0DEF0";

// ── Tone palette ──────────────────────────────────────────────────────────
type Tone = "sky" | "indigo" | "amber" | "slate" | "emerald" | "violet" | "teal" | "rose";

const TONE_CLASSES: Record<Tone, { bg: string; ring: string; ink: string; chip: string }> = {
  sky:     { bg: "bg-sky-50",     ring: "ring-sky-200",     ink: "text-sky-700",     chip: "bg-sky-100 text-sky-700" },
  indigo:  { bg: "bg-indigo-50",  ring: "ring-indigo-200",  ink: "text-indigo-700",  chip: "bg-indigo-100 text-indigo-700" },
  amber:   { bg: "bg-amber-50",   ring: "ring-amber-200",   ink: "text-amber-700",   chip: "bg-amber-100 text-amber-700" },
  slate:   { bg: "bg-slate-100",  ring: "ring-slate-200",   ink: "text-slate-700",   chip: "bg-slate-200 text-slate-700" },
  emerald: { bg: "bg-emerald-50", ring: "ring-emerald-200", ink: "text-emerald-700", chip: "bg-emerald-100 text-emerald-700" },
  violet:  { bg: "bg-violet-50",  ring: "ring-violet-200",  ink: "text-violet-700",  chip: "bg-violet-100 text-violet-700" },
  teal:    { bg: "bg-teal-50",    ring: "ring-teal-200",    ink: "text-teal-700",    chip: "bg-teal-100 text-teal-700" },
  rose:    { bg: "bg-rose-50",    ring: "ring-rose-200",    ink: "text-rose-700",    chip: "bg-rose-100 text-rose-700" },
};

// ── Decks ─────────────────────────────────────────────────────────────────
type DeckCard = {
  slug: string;
  mark: string;
  titleLine1: string;
  titleAccent: string;
  sub: string;
  slides: string;
};

const decks: DeckCard[] = [
  {
    slug: "elad/dashboard-intro",
    mark: "Your Workspace",
    titleLine1: "Welcome,",
    titleAccent: "Elad.",
    sub: "A short tour of what you've just stepped into — your dashboard, your knowledge, and how the agents fit in.",
    slides: "5 slides · with voice",
  },
  {
    slug: "elad/first-ticket",
    mark: "Tutorial",
    titleLine1: "Your",
    titleAccent: "first ticket.",
    sub: "From terminal greeting to a closed ticket in the dashboard. Step by step.",
    slides: "5 slides · with voice",
  },
  {
    slug: "elad/roadmap",
    mark: "Strategic Roadmap",
    titleLine1: "The road",
    titleAccent: "ahead.",
    sub: "Where your dashboard, your work, and the platform are going. This week, next month, this year.",
    slides: "5 slides · with voice",
  },
];

// ── Action buttons ────────────────────────────────────────────────────────
type ActionBtn = {
  label: string;
  command: string;
};

const actionButtons: ActionBtn[] = [
  { label: "Install bun", command: "curl -fsSL https://bun.sh/install | bash" },
  { label: "Install the plugin", command: "/plugin marketplace add erezgit/myjarvis-os-plugin && /plugin install myjarvis-os@myjarvis-os-plugin" },
  { label: "Tell Jarvis who you are", command: "Read https://raw.githubusercontent.com/erezgit/myjarvis-os-plugin/main/CLAUDE.md.template and adapt it to my workspace: set CLAUDE_PROJECT_DIR to ./ , swap {{OPERATOR_NAME}} with my name, save to ./CLAUDE.md." },
  { label: "Wire your dashboard", command: "claude mcp add --transport http my-jarvis-mcp https://my-jarvis-mcp.myjarvis.workers.dev/mcp" },
];

function ActionButton({ label, command }: ActionBtn) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-between rounded-md border bg-white/70 px-3 py-2 text-xs font-medium transition-colors hover:bg-white"
      style={{ borderColor: LINE, color: SKY_DARK }}
    >
      <span>{copied ? "Copied" : label}</span>
      <span className="ml-2">↗</span>
    </button>
  );
}

// ── Section header ────────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title, sub, tone = "sky" }: { icon: LucideIcon; title: string; sub?: string; tone?: Tone }) {
  const c = TONE_CLASSES[tone];
  return (
    <div className="mb-4 mt-10 flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${c.bg}`}>
        <Icon className={`h-5 w-5 ${c.ink}`} />
      </div>
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-stone-900">{title}</h2>
        {sub && <p className="text-sm text-stone-500">{sub}</p>}
      </div>
    </div>
  );
}

// ── Tile card ─────────────────────────────────────────────────────────────
function Tile({
  icon: Icon,
  title,
  desc,
  chips,
  tone,
  href,
  meta,
  featured,
}: {
  icon: LucideIcon;
  title: string;
  desc?: string;
  chips?: string[];
  tone: Tone;
  href?: string;
  meta?: ReactNode;
  featured?: boolean;
}) {
  const c = TONE_CLASSES[tone];
  const inner = (
    <div
      className={`flex h-full flex-col rounded-xl border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        featured ? "ring-2 " + c.ring : "ring-1 ring-stone-200"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${c.bg}`}>
          <Icon className={`h-5 w-5 ${c.ink}`} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold leading-tight text-stone-900">{title}</h3>
          {desc && <p className="mt-1 text-sm leading-relaxed text-stone-500">{desc}</p>}
        </div>
      </div>
      {chips && chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {chips.map((ch) => (
            <span key={ch} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${c.chip}`}>
              {ch}
            </span>
          ))}
        </div>
      )}
      {meta && <div className="mt-3 border-t pt-2 text-xs text-stone-400">{meta}</div>}
    </div>
  );
  return href ? <Link to={href} className="block h-full">{inner}</Link> : inner;
}

// ── Page ──────────────────────────────────────────────────────────────────
export function HomePage() {
  return (
    <div className="w-full">
      {/* Banner */}
      <div className="relative mb-6 h-[420px] overflow-hidden rounded-xl bg-muted">
        <img
          src={BANNER_URL}
          alt="A field at sunset — your workspace, Elad."
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover object-bottom"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-8 pb-8">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80 drop-shadow">
            Welcome, Elad
          </div>
          <h1 className="mt-2 text-4xl font-bold text-white drop-shadow-md md:text-5xl">
            Water Infrastructure · SCADA · Instrumentation & Control
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/85 drop-shadow">
            Twelve work tickets, twelve product articles, eight manuals, five client sites. All your knowledge, in one place.
          </p>
        </div>
      </div>

      {/* Title row — heading on left, action buttons on right */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">For Elad</h2>
          <p className="mt-1 text-muted-foreground">
            Three short decks. Open each to listen and read along.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 md:justify-end">
          {actionButtons.map((b) => (
            <ActionButton key={b.label} label={b.label} command={b.command} />
          ))}
        </div>
      </div>

      {/* Row of 3 deck cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
        {decks.map((d) => (
          <Link
            key={d.slug}
            to={`/pitch-doc/${d.slug}`}
            className="group relative flex h-72 flex-col overflow-hidden rounded-xl border p-7 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            style={{
              background: `linear-gradient(135deg, ${BG} 0%, ${BG2} 100%)`,
              borderColor: LINE,
            }}
          >
            <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: SKY_DARK }}>
              {d.mark}
            </div>
            <h3 className="mt-8 text-3xl font-semibold leading-[1.05] tracking-tight text-stone-900">
              {d.titleLine1}
              <br />
              <span className="font-medium italic" style={{ color: SKY_DARK }}>
                {d.titleAccent}
              </span>
            </h3>

            <div className="mt-auto pt-6">
              <p className="text-sm leading-relaxed text-stone-600 line-clamp-2">{d.sub}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs font-medium text-stone-400">{d.slides}</span>
                <span className="text-xs font-semibold transition-transform group-hover:translate-x-0.5" style={{ color: SKY_DARK }}>
                  Open deck →
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* ── Client Sites ────────────────────────────────────────────────── */}
      <SectionHeader
        icon={MapPin}
        title="Client Sites"
        sub="Five active sites · twelve total tickets across the lifecycle."
        tone="emerald"
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={Droplet}
          title="Mishmar HaEmek"
          desc="Pump control system, 0–5m range. Code + documentation."
          chips={["1 ticket", "PLC code", "0–5m"]}
          tone="emerald"
          href="/kb-doc/tickets/007-mishmar-haemek"
          meta="Pump control code (5m + 0–5m), code documentation, README"
        />
        <Tile
          icon={Activity}
          title="Arazim"
          desc="Volume analysis + monthly reports automation. 49 tanks, station registry."
          chips={["2 tickets", "16 files", "Volume + reports"]}
          tone="sky"
          href="/kb-doc/tickets/009-arazim-volume-analysis"
          featured
          meta="9-file analysis + 9-file monthly reporting (Dec 2025, Jan 2026)"
        />
        <Tile
          icon={Gauge}
          title="Selem"
          desc="Volume analysis — station search + configuration."
          chips={["1 ticket", "Volume analysis"]}
          tone="amber"
          href="/kb-doc/tickets/011-selem-volume-analysis"
        />
        <Tile
          icon={Zap}
          title="Newton"
          desc="Dynamic pressure control — roadmap, field testing, macro commands."
          chips={["1 ticket", "4 HTMLs", "Pressure control"]}
          tone="rose"
          href="/kb-doc/tickets/012-newton-dynamic-pressure-control"
        />
      </div>

      {/* ── Product Family ─────────────────────────────────────────────── */}
      <SectionHeader
        icon={Droplet}
        title="Product Family — Hidroline & Hardware Library"
        sub="The technical docs, datasheets, and manuals you sell + spec from."
        tone="sky"
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Tile
          icon={Droplet}
          title="Hidroline — water pressure system"
          desc="Datasheets, environmental resilience, operating principles, sensitivity & accuracy, TRL market-readiness, plus the Hebrew TRL explainer. Your flagship line."
          chips={["10 articles", "EN + Spanish + Hebrew", "TRL analysis", "Product sheet v3"]}
          tone="sky"
          href="/kb-doc/product-library"
          featured
          meta="Includes original PDFs + chunked text for search · click through to the full library."
        />
        <Tile
          icon={Cog}
          title="Workflow — Station Volume Analysis"
          desc="The end-to-end playbook for pulling station volumes from Zeus and producing client-ready monthly reports."
          chips={["Playbook", "Zeus → report"]}
          tone="teal"
          href="/kb-doc/product-library/workflow-station-volume-analysis-md"
        />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={Wrench}
          title="Apollo Configuration"
          desc="Manual v8 EN Rev 5.2020. Configuration software reference."
          chips={["20 chunks", "PDF + text"]}
          tone="amber"
          href="/kb-doc/product-library/manuals/apollo-configuration-soft-manual-v8-en-rev-5-2020"
        />
        <Tile
          icon={Cpu}
          title="microPLC II"
          desc="PLC firmware programming reference, English."
          chips={["9 chunks", "PDF + text"]}
          tone="slate"
          href="/kb-doc/product-library/manuals/microplc-ii-en"
        />
        <Tile
          icon={Radio}
          title="POWTRAN PI150"
          desc="VFD frequency converter manual."
          chips={["4 chunks", "VFD"]}
          tone="violet"
          href="/kb-doc/product-library/manuals/powtran-pi150"
        />
        <Tile
          icon={Globe}
          title="Hydra v1 (ES)"
          desc="Solución Hydra — Spanish product line documentation."
          chips={["2 chunks", "Español"]}
          tone="rose"
          href="/kb-doc/product-library/manuals/soluci-n-hydra-v1"
        />
      </div>

      {/* ── Integrations ───────────────────────────────────────────────── */}
      <SectionHeader
        icon={Plug}
        title="Integrations"
        sub="External platforms you connect to. Zeus is the big one."
        tone="indigo"
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Tile
          icon={Server}
          title="Zeus API — Raphael Valves SCADA"
          desc="Python client + tests + station registry (all_stations.json). The integration that powers Arazim volume analysis + monthly reports. Live calls into the dashboard are next on the roadmap."
          chips={["Python module", "Bearer auth", "REST", "Station registry"]}
          tone="indigo"
          href="/kb-doc/zeus-connection"
          featured
          meta="ZeusAPI.py · test_connection.py · zeus_api_test.py · JARVIS_INSTRUCTIONS · README"
        />
        <Tile
          icon={Radio}
          title="Milesight UC502 — IoT Gateway"
          desc="Cellular IoT gateway overview. Field-deployable telemetry."
          chips={["IoT", "Cellular", "Overview"]}
          tone="teal"
          href="/kb-doc/milesight"
        />
      </div>

      {/* ── Capabilities ───────────────────────────────────────────────── */}
      <SectionHeader
        icon={ShieldCheck}
        title="What you can do"
        sub="Capabilities the dashboard makes accessible — to you and (later) to your customers."
        tone="violet"
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        <Tile
          icon={Cog}
          title="Industrial Control + PLC Programming"
          desc="Hermes 103, microPLC II, analog I/O calibration, frequency converter (VFD) configuration."
          chips={["4 tickets", "PLC", "Calibration", "VFD"]}
          tone="slate"
        />
        <Tile
          icon={Server}
          title="SCADA Integration"
          desc="Zeus API client, station data export, historical volume retrieval, authenticated REST calls."
          chips={["Zeus", "Python", "Bearer"]}
          tone="indigo"
        />
        <Tile
          icon={Activity}
          title="Volume Analysis + Reporting"
          desc="Monthly per-site reports (Arazim style) — pull, analyse, generate client-ready PDFs."
          chips={["Monthly", "PDF reports", "Analysis"]}
          tone="sky"
        />
        <Tile
          icon={Droplet}
          title="Water Infrastructure (Hidroline)"
          desc="Pressure sensor systems, TRL assessment, multi-site pump + pressure control deployment."
          chips={["Hidroline", "TRL", "Multi-site"]}
          tone="emerald"
        />
        <Tile
          icon={Plug}
          title="IoT Gateway + Field Telemetry"
          desc="Milesight UC502 cellular gateway, remote monitoring, web-based data access."
          chips={["IoT", "Cellular", "Field"]}
          tone="teal"
        />
        <Tile
          icon={FileText}
          title="Technical Documentation"
          desc="Bilingual Hebrew + English documentation, PDF text extraction pipeline, presentation generation."
          chips={["HE + EN", "PDF extraction", "Presentations"]}
          tone="amber"
        />
      </div>

      {/* ── Recent Tickets — last 5 in chronological order ──────────────── */}
      <SectionHeader
        icon={ClipboardList}
        title="Recent Work"
        sub="Latest tickets from your work archive."
        tone="amber"
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        <Tile
          icon={Zap}
          title="012 · Newton — Dynamic Pressure Control"
          desc="Roadmap + dynamic pressure control guide + field testing procedure + macro commands."
          chips={["4 HTMLs", "Field testing"]}
          tone="rose"
          href="/kb-doc/tickets/012-newton-dynamic-pressure-control"
        />
        <Tile
          icon={Gauge}
          title="011 · Selem — Volume Analysis"
          desc="Goal definition, station config, station search results JSON."
          chips={["3 files", "Volume"]}
          tone="amber"
          href="/kb-doc/tickets/011-selem-volume-analysis"
        />
        <Tile
          icon={Activity}
          title="010 · Arazim — Monthly Reports"
          desc="December 2025 + January 2026 reports (HTML + PDF), monthly data archive, analysis summary."
          chips={["9 files", "Monthly", "PDF + HTML"]}
          tone="sky"
          href="/kb-doc/tickets/010-arazim-monthly-reports"
        />
        <Tile
          icon={Activity}
          title="009 · Arazim — Volume Analysis"
          desc="Goal definition, raw data JSON, station config, analysis script, final report (HTML + PDF)."
          chips={["7 files", "Python script"]}
          tone="sky"
          href="/kb-doc/tickets/009-arazim-volume-analysis"
        />
        <Tile
          icon={Server}
          title="008 · Zeus API Integration"
          desc="Roadmap, discovery requirements, JARVIS_INSTRUCTIONS, Python client + tests."
          chips={["8 files", "Python", "Integration"]}
          tone="indigo"
          href="/kb-doc/tickets/008-zeus-api-integration"
        />
        <Tile
          icon={Droplet}
          title="002 · Dairy Separator System"
          desc="The heaviest ticket — 20 files: 4-20mA conversion, anomalous-identifier fix, I/O mapping v1+v2, page-by-page docs, simulation test plan."
          chips={["20 files", "Heaviest"]}
          tone="emerald"
          href="/kb-doc/tickets/002-dairy-separator-system"
          featured
        />
      </div>
    </div>
  );
}

HomePage.path = "/home";
